import { Option, Value } from 'baseui/select';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import { ColumnDataType, IColumn } from 'components/elements/table/type';
import Text from 'components/elements/text';
import { SeparatorVertical } from 'components/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { addNewAddOns } from '../../hooks/use-get-default-values';
import {
  TourSaleItemAddOnsFormType,
  TourSaleItemGuestFormType,
} from '../form-type';

interface Props {
  parentIndex: number;
}

function useGetTourAddOnsTableList(): IColumn<TourSaleItemAddOnsFormType>[] {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { editable } = useFormState();
  const guests: TourSaleItemGuestFormType[] = useWatch({
    control,
    name: 'tourSales.0.tourSaleGuests',
  });
  const guestList: Option[] = guests
    .filter((guest) => guest.guestId)
    .map((guest) => {
      return {
        id: guest.guestId,
        label: guest.guestName,
      };
    });
  return [
    {
      header: t('common:name'),
      accessorKey: 'guestId',
      minSize: 250,
      dataType: ColumnDataType.select,
      options: guestList,
    },
    {
      header: t('common:description'),
      accessorKey: 'description',
      minSize: 250,
      dataType: ColumnDataType.text,
    },
    {
      header: t('common:price'),
      accessorKey: 'price',
      minSize: 250,
      dataType: ColumnDataType.number,
      minNumber: 0,
    },
    ...deleteColumn(editable),
  ];
}

export function TourAddOnsTable(props: Props) {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const { clearErrors, setValue, control, trigger, getValues } =
    useFormContext();

  const name = 'tourSales.0.tourSaleAddOns' as const;

  const columns = useGetTourAddOnsTableList();

  const addons = useWatch({
    name,
    control,
  }) as TourSaleItemAddOnsFormType[];

  const dispatch = React.useCallback(
    (action) => {
      const currentRowIndex = action.rowIndex;
      const addOns = getValues(name) as TourSaleItemAddOnsFormType[];
      const addOn = addOns[currentRowIndex];
      const columnId = action.columnId as keyof TourSaleItemAddOnsFormType;
      const extra = action.extra as Value;

      const otherVal: () => Partial<TourSaleItemAddOnsFormType> = () => {
        if (columnId === 'guestId') {
          return { guestName: extra?.[0]?.label as string };
        }
        return {};
      };

      if (action.type === 'addRow') {
        const data = [...addOns, addNewAddOns()];
        setValue(name, data);
        trigger(name);
      } else if (action.type === 'removeRow') {
        addOns.splice(currentRowIndex, 1);
        setValue(name, addOns);
        trigger(name);
      } else if (action.type === 'update_cell') {
        addOns[currentRowIndex] = {
          ...addOn,
          [action.columnId]: action.value,
          ...otherVal(),
        };
        setValue(name, addOns);
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [clearErrors, getValues, name, setValue, trigger],
  );
  if ((addons || []).length || editable) {
    return (
      <div style={{ width: '100%' }}>
        <Text variant="LabelMedium">{t('common:add_ons')}</Text>
        <SeparatorVertical $margin={16} />
        <TableInput
          columns={columns}
          data={addons}
          dispatch={dispatch}
          disabled={!editable}
          name={name}
          uniqueRowKey="customId"
        />
      </div>
    );
  }

  return <></>;
}
